import React, { useState } from 'react';
import { TrendingUp, TrendingDown, ChevronDown, ChevronUp } from 'lucide-react';
import './MobileBriefing.css';

interface KeyMetrics {
    brvm_c: { value: number; change: number };
    brvm_30: { value: number; change: number };
    brvm_prestige?: { value: number; change: number };
    volume: number;
    cap: number;
    top_gainers: Array<{ symbol: string; change: number }>;
    top_losers: Array<{ symbol: string; change: number }>;
    most_active: Array<{ symbol: string; value: number }>;
}

interface Briefing {
    id: number;
    date_briefing: string;
    market_mood: string;
    content_html: string;
    key_metrics: string;
    created_at: string;
    whatsapp_content?: string;
}

interface MobileBriefingProps {
    briefing: Briefing;
    metrics: KeyMetrics;
    downloadPDF: () => void;
    downloadingPdf: boolean;
    isAdmin: boolean;
    onWhatsAppShare: () => void;
    whatsappCopied: boolean;
}

const MobileBriefing: React.FC<MobileBriefingProps> = ({
    briefing,
    metrics,
    downloadPDF,
    downloadingPdf,
    isAdmin,
    onWhatsAppShare,
    whatsappCopied,
}) => {
    const [moverTab, setMoverTab] = useState<'tops' | 'flops'>('tops');
    const [analysisExpanded, setAnalysisExpanded] = useState(true); // Text is the primary content

    const moodConfig = {
        Bullish: { emoji: '🟢', label: 'Haussière', color: '#22c55e', bg: 'rgba(34, 197, 94, 0.08)' },
        Bearish: { emoji: '🔴', label: 'Baissière', color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)' },
        Neutral: { emoji: '⚪', label: 'Neutre', color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.08)' },
    };
    const mood = moodConfig[briefing.market_mood as keyof typeof moodConfig] || moodConfig.Neutral;

    const activeList = moverTab === 'tops' ? metrics.top_gainers : metrics.top_losers;
    const activeColor = moverTab === 'tops' ? '#22c55e' : '#ef4444';

    return (
        <div className="mb-container">

            {/* 1. MOOD HERO — The #1 signal */}
            <div className="mb-mood-hero" style={{ backgroundColor: mood.bg }}>
                <div className="mb-mood-date">
                    📅 {new Date(briefing.date_briefing).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
                </div>
                <div className="mb-mood-badge" style={{ color: mood.color }}>
                    {mood.emoji} Ambiance {mood.label}
                </div>
                <button
                    onClick={downloadPDF}
                    disabled={downloadingPdf}
                    className="mb-pdf-btn"
                >
                    {downloadingPdf ? '⏳ Téléchargement...' : '📄 Télécharger le PDF'}
                </button>
                {isAdmin && briefing.whatsapp_content && (
                    <button
                        onClick={onWhatsAppShare}
                        className="mb-pdf-btn"
                        style={{
                            marginTop: '8px',
                            backgroundColor: whatsappCopied ? '#22c55e' : 'rgba(37, 211, 102, 0.1)',
                            border: '1px solid #25d366',
                            color: whatsappCopied ? 'white' : '#25d366',
                            fontWeight: '600',
                            cursor: 'pointer'
                        }}
                    >
                        {whatsappCopied ? '✅ Copié !' : '📱 Partager sur WhatsApp'}
                    </button>
                )}
            </div>

            {/* 2. KPI CARDS — 3 indices in a row */}
            <div className="mb-kpi-row">
                <div className="mb-kpi-card">
                    <span className="mb-kpi-label">Composite</span>
                    <span className="mb-kpi-value">{metrics.brvm_c.value}</span>
                    <span className={`mb-kpi-change ${metrics.brvm_c.change >= 0 ? 'positive' : 'negative'}`}>
                        {metrics.brvm_c.change >= 0 ? '+' : ''}{metrics.brvm_c.change}%
                    </span>
                </div>
                {metrics.brvm_prestige && (
                    <div className="mb-kpi-card">
                        <span className="mb-kpi-label">Prestige</span>
                        <span className="mb-kpi-value">{metrics.brvm_prestige.value}</span>
                        <span className={`mb-kpi-change ${metrics.brvm_prestige.change >= 0 ? 'positive' : 'negative'}`}>
                            {metrics.brvm_prestige.change >= 0 ? '+' : ''}{metrics.brvm_prestige.change}%
                        </span>
                    </div>
                )}
                <div className="mb-kpi-card">
                    <span className="mb-kpi-label">Volume</span>
                    <span className="mb-kpi-value">{(metrics.volume / 1_000_000).toFixed(0)} M</span>
                    <span className="mb-kpi-change neutral">FCFA</span>
                </div>
            </div>

            {/* 3. AI ANALYSIS — Primary content */}
            <div className="mb-analysis">
                <button
                    className="mb-analysis-toggle"
                    onClick={() => setAnalysisExpanded(!analysisExpanded)}
                >
                    <span>📝 Analyse détaillée par Nia</span>
                    {analysisExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                </button>
                {analysisExpanded && (
                    <div className="mb-analysis-content">
                        <div
                            className="briefing-content"
                            dangerouslySetInnerHTML={{ __html: briefing.content_html }}
                        />
                        <div className="mb-analysis-footer">
                            Ce briefing est généré quotidiennement par Nia, notre IA, et revu par notre équipe d'analystes.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MobileBriefing;
